'use client';

import { getRandomPithyActivityStatement } from '@/lib/client-utils';
import { isCompleteStatus } from '@/lib/clientsafe-utils';
import StyledSpinner from "app/components/StyledSpinner";
import { classNames } from 'lib/generic-utils';
import Image from 'next/image';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { IoVolumeHigh, IoVolumeMute } from "react-icons/io5";
import { AnimationDisplayData } from './animate-utils';
import { log } from '@logtail/next';

const MAX_PREVIEW_HEIGHT = 576;
const MAX_PREVIEW_WIDTH = 1024;

type PreviewPaneProps = {
    displayData: AnimationDisplayData | undefined;
    pageState: 'BLANK' | 'SUBMITTING' | 'PROCESSING' | 'COMPLETED';
    latestFrameUrl: string | undefined;
    bestVideoUrl: string | undefined;
}

export default function PreviewPane({ displayData, pageState, latestFrameUrl, bestVideoUrl }: PreviewPaneProps) {

    const [pithyActivityStatement, setPithyActivityStatement] = useState<string | undefined>();

    const videoRef = useRef<HTMLVideoElement>(null);
    const [videoDuration, setVideoDuration] = useState(0);   
    const [userWantsFrameOrVideo, setUserWantsFrameOrVideo] = useState<'frame'|'video'|'auto'>('auto');
    const [videoIsLoading, setVideoIsLoading] = useState(false);
    const [isMuted, setIsMuted] = useState(true);
    const [videoLoadErrorMessage, setVideoLoadErrorMessage] = useState<string|undefined>();
    
    const previewDimensions = useMemo(() => {
        if (!displayData?.width || !displayData?.height) {
            // default to square
            return { width: MAX_PREVIEW_HEIGHT, height: MAX_PREVIEW_HEIGHT };
        }
        const aspectRatio = displayData.height / displayData.width;
        let width = displayData.width;
        let height = displayData.height;

        if (width > MAX_PREVIEW_WIDTH) {
            width = MAX_PREVIEW_WIDTH;
            height = width * aspectRatio;
        }

        if (height > MAX_PREVIEW_HEIGHT) {
            height = MAX_PREVIEW_HEIGHT;
            width = height / aspectRatio;
        }

        return { width, height };
    }, [displayData?.width, displayData?.height]);


    const toggleMute = useCallback(() => {
        if (videoRef.current) {
            videoRef.current.muted = !videoRef.current.muted;
            setIsMuted(videoRef.current.muted);
        }
    }, []);

    // Update isMuted state when video is muted/unmuted
    useEffect(() => {
        const videoElement = videoRef.current;
        if (videoElement) {
            const handleMuteChange = () => {
                setIsMuted(videoElement.muted);
            };
            videoElement.addEventListener('volumechange', handleMuteChange);

            return () => {
                videoElement.removeEventListener('volumechange', handleMuteChange);
            };
        }
    }, []);

    useEffect(() => {
        if (videoRef.current && !bestVideoUrl) {
            // There is no more video. Stop playback if we had one loaded from a prior animation.
            videoRef.current.pause();
            videoRef.current.currentTime = 0;
            videoRef.current.muted = true;
            setIsMuted(true);            
        }

        if (videoRef.current && bestVideoUrl) {
            // A new video has arrived!
            const video = videoRef.current;
            const wasPlaying = !video.paused;
            const currentTime = video.currentTime;
            const volume = video.volume;
            const muted = video.muted;

            setVideoIsLoading(true);
            video.src = bestVideoUrl;

            video.onloadedmetadata = () => {
                video.currentTime = currentTime;
                video.volume = volume;
                video.muted = muted;
                setIsMuted(muted);  // Update isMuted state when video source changes
                if (wasPlaying) {
                    video.play().catch((error:unknown) => log.error("Error playing video:", {error}));
                }
                setVideoDuration(video.duration);
            };
        }
    }, [bestVideoUrl]);


    const showFrameOrVideo = useMemo(() => {
        if (!bestVideoUrl || videoIsLoading || userWantsFrameOrVideo === 'frame') {
            return 'frame';
        } else {
            return 'video';
        }
    }, [bestVideoUrl, userWantsFrameOrVideo, videoIsLoading]);


    useEffect(() => {
        if (videoRef.current && bestVideoUrl) {
            const video = videoRef.current;
            const wasPlaying = !video.paused;
            const currentTime = video.currentTime;
            const volume = video.volume;
            const muted = video.muted;

            // Change the src
            setVideoIsLoading(true);
            video.src = bestVideoUrl;

            // After the source is loaded, restore the state
            video.onloadedmetadata = () => {
                video.currentTime = currentTime;
                video.volume = volume;
                video.muted = muted;
                if (wasPlaying) {
                    video.play().catch((error:unknown) => log.error("Browser error: Error playing video:", {error}));
                }
                setVideoDuration(video.duration);
                setVideoLoadErrorMessage(undefined);
            };
        }
    }, [bestVideoUrl]);


    // Periodically update pithy activity statement
    useEffect(() => {
        if (latestFrameUrl || !['SUBMITTING', 'PROCESSING'].includes(pageState)) {
            return;
        }
        const intervalId = setInterval(() => { setPithyActivityStatement(getRandomPithyActivityStatement()) }, 9000);
        return () => clearInterval(intervalId);
    }, [latestFrameUrl, pageState]);

    const frameImage = useMemo(() => latestFrameUrl
        ? <Image
            alt='latest frame'
            src={latestFrameUrl}
            width={previewDimensions.width}
            height={previewDimensions.height}
        />
        : <Image
            alt='placeholder'
            src={previewDimensions.width == previewDimensions.height ? '/Blur-sq.png' : previewDimensions.width > previewDimensions.height ? '/Blur-ls.png' : '/Blur-pt.png'}
            width={previewDimensions.width}
            height={previewDimensions.height}
        />, [latestFrameUrl, previewDimensions.width, previewDimensions.height]);


    const frameMessageOverlay = useMemo(() => {

        let message;
        switch (displayData?.status) {
            case undefined:
                // If there is no status, we ar either on a blank animation or user has just submitted.
                message = (pageState === 'SUBMITTING')
                    ? <><p className="py-4">Your animation request has been sent. Hold tight!</p>
                        <StyledSpinner className="animate-spin-slow mt-3 mx-auto" />
                        <p className="py-4 text-black dark:text-white text-xs">{pithyActivityStatement}</p>
                    </>
                    : <p>Fill out the options and hit <strong>ANIMATE</strong> to generate a new animation.</p>
                break;
            case 'failed':
                message = <p>Looks like something went wrong. :(</p>
                break;
            case 'canceled':
                message = <p>Animation canceled.</p>
                break;
            default:
                if (!latestFrameUrl) {
                    message = <>
                        {(pageState === 'SUBMITTING') && <p className="py-4">Your animation request has been sent. Hold tight!</p>}
                        {(pageState === 'PROCESSING') && <p className="py-4">Your animation is queued and will generate shortly. You will see a preview here soon! Feel free to kick off another one, or leave this page and check your library later.</p>}
                        <StyledSpinner className="animate-spin-slow mt-3 mx-auto" />
                        <p className="py-4 text-black dark:text-white text-xs">{pithyActivityStatement}</p>
                    </>
                }
        }

        return message ? <div id="frameMessageOverlay" className="absolute inset-0  bg-neutral-100 opacity-50 dark:bg-neutral-900 flex justify-center items-center pointer-events-none z-10">
            <div className='p-4 text-center'>
                {message}
            </div>
        </div> : <></>

    }, [displayData?.status, latestFrameUrl, pageState, pithyActivityStatement]);

    const frameNumberOverlay = useMemo(() => latestFrameUrl ? <div id="frameNumberOverlay" className="absolute top-0 left-0 right-0 bottom-0 flex justify-end items-start pointer-events-none">
            <p className="text-white text-sm bg-neutral-500 bg-opacity-70 p-2 [text-shadow:_0_1px_0_rgb(0_0_0_/_40%)]">Frame {getFrameNumberFromUrl(latestFrameUrl)}/{displayData?.expectedFrameCount}</p>
    </div> : <></>, [displayData?.expectedFrameCount, latestFrameUrl]);


    const videoTypeOverlay = useMemo(() => {
        const isPreview = bestVideoUrl?.includes('preview');
        return (
            <div 
                id="videoTypeOverlay" 
                className="absolute top-0 right-0 flex justify-end items-start pointer-events-auto cursor-pointer"
                onClick={toggleMute}
            >
                <div className={classNames(
                    'flex items-center space-x-2 bg-opacity-70 p-2 [text-shadow:_0_1px_0_rgb(0_0_0_/_40%)]',
                    isPreview ? 'bg-neutral-500' : 'bg-green-600'
                )}>
                    {isPreview
                        ? <p className='text-white text-xs'>Preview ({videoDuration.toFixed(2)}s)</p>
                        : <p className='text-white text-sm'>Final video</p>}
                    {displayData?.isAudioReactive && (
                        isMuted 
                            ? <IoVolumeMute size={16} color="red" aria-label="Unmute" />
                            : <IoVolumeHigh size={16} color="white" aria-label="Mute" />
                    )}
                </div>
            </div>
        );
    }, [bestVideoUrl, videoDuration, isMuted, displayData?.isAudioReactive, toggleMute]);
    
    const videoMessageOverlay = useMemo(() => videoLoadErrorMessage
        ? <div id="frameMessageOverlay" className="absolute inset-0  bg-neutral-100 opacity-50 dark:bg-neutral-900 flex justify-center items-center pointer-events-none z-10">
            <div className='p-4 text-center'>
                {videoLoadErrorMessage}
            </div>
        </div>
        : <></>
    , [videoLoadErrorMessage]);

    const frameVideoToggle = useMemo(() => {
        if (!latestFrameUrl || !bestVideoUrl) {
            return <></>;
        }
        const toggleTo = showFrameOrVideo === 'frame' ? 'video' : 'frame';
        return <div className="absolute top-0 left-0 flex justify-start items-start cursor-pointer w-min h-min">
            <div
                className={classNames('text-white  text-xs font-thin bg-opacity-70 p-2 [text-shadow:_0_1px_0_rgb(0_0_0_/_40%)] bg-neutral-500')}
                onClick={() => setUserWantsFrameOrVideo(toggleTo)}>
                Show&nbsp;{toggleTo} 
            </div>
        </div>
    }, [bestVideoUrl, latestFrameUrl, showFrameOrVideo]);


    return (
        <div className="w-full" style={{ 
            maxWidth: `${previewDimensions.width}px`,
            aspectRatio: `${previewDimensions.width} / ${previewDimensions.height}`,
        }}>
            <div className="relative w-full h-full">
                {/* Frame view */}
                <div className={classNames('absolute inset-0', showFrameOrVideo === 'frame' ? 'block' : 'hidden')}>
                    {frameImage}
                    {frameMessageOverlay}
                    {frameNumberOverlay}
                    {!videoIsLoading && frameVideoToggle}
                    {videoIsLoading && (
                        <div className="absolute inset-0 flex items-center justify-center bg-neutral-500 bg-opacity-20">
                            <p className='text-white text-xs'>Updating video...</p>
                            <StyledSpinner className="animate-spin-slow" />
                        </div>
                    )}            
                </div>
                {/* Video view */}
                <div className={classNames('absolute inset-0', showFrameOrVideo === 'video' ? 'block' : 'hidden')}>
                    <video
                        ref={videoRef}
                        controls={true}
                        loop={true}
                        muted={isMuted}
                        autoPlay={false}
                        onLoadStart={() => setVideoIsLoading(true)}
                        onCanPlay={() => setVideoIsLoading(false)}
                        className="w-full h-full object-contain"
                        onError={(error) => {
                            log.error("Browser error: Error loading video:", {error});
                            setVideoIsLoading(false);
                            setVideoLoadErrorMessage("Oops! Something went wrong loading the video. Try the 'Download video' button below, or reloading the page, or come back in a bit. If the issue persists, drop us an email.");
                        }}
                    />
                    {videoMessageOverlay}
                    {videoTypeOverlay}
                    {displayData && !isCompleteStatus(displayData?.status) && frameVideoToggle}
                </div>
            </div>
        </div>
    );
}

// If this ever turns out to be hassle, we could also just pass the frame number to the component...
function getFrameNumberFromUrl(url: string) {
    const match = url.match(/.*frame.*?(\d+)\..*/);
    const frame = match ? parseInt(match[1]) : undefined;   
    return frame;
}

